import React from "react";
import PropTypes from "prop-types";
import { useMutation } from "react-query";
import { useRouter } from "next/router";
import { inBrowser } from "@gonoodle/gn-universe-utils";
import { validate } from "@gonoodle/gn-universe-analytics-schema";
import flowRight from "lodash/flowRight";

import BugsnagClient from "../../utils/bugsnag";
import { getAnalyticsParams } from "../../utils/analytics";
import { sendEvent } from "./core";

const JSURL = require("jsurl");

const AnalyticsContext = React.createContext();
AnalyticsContext.displayName = "AnalyticsContext";

export function getReferrerFromRouter(router) {
  if (inBrowser()) {
    return JSURL.parse(router.query.referrer);
  }
  return undefined;
}

function reportError({ properties, event, error }) {
  const errorMessage = `Invalid ${event} event properties, ${error}`;
  if (error && process.env.NODE_ENV === "development") {
    throw new Error(errorMessage);
  } else if (error) {
    BugsnagClient.notify(errorMessage);
  }
  return {
    properties,
    event,
  };
}

export function AnalyticsProvider({ children, options }) {
  return (
    <AnalyticsContext.Provider value={options}>
      {children}
    </AnalyticsContext.Provider>
  );
}

function useAnalytics() {
  const context = React.useContext(AnalyticsContext);
  if (context === undefined) {
    throw new Error("You forgot to wrap your app in a AnalyticsProvider");
  }
  return context;
}

export function useLogEvent({ event, properties, options }) {
  const router = useRouter();
  const { enabled = true, referrer } = options;
  const sent = React.useRef(false);
  const { doNotTrack } = useAnalytics();

  const {
    mutate: logEvent,
    isIdle,
    isLoading,
    isSuccess,
    isError,
  } = useMutation(
    ({ event: eventname, properties: eventAttributes }) =>
      sendEvent(eventname, eventAttributes),
    { retry: 3 },
  );

  React.useEffect(() => {
    if (!doNotTrack && enabled && event && sent.current === false) {
      sent.current = true;
      const logEventWithValidation = flowRight(logEvent, reportError, validate);
      logEventWithValidation(event, {
        ...(typeof referrer === "function"
          ? referrer(getAnalyticsParams(getReferrerFromRouter(router)))
          : getAnalyticsParams(
              referrer || getReferrerFromRouter(router) || router.query,
            )),
        ...properties,
      });
    }
  }, [enabled, doNotTrack, event, router, properties, logEvent, referrer]);
  return {
    isIdle,
    isLoading,
    isSuccess,
    isError,
  };
}

AnalyticsProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  options: PropTypes.shape({
    doNotTrack: PropTypes.bool,
  }),
};

AnalyticsProvider.defaultProps = {
  options: {
    doNotTrack: false,
  },
};
